import type { GuidedState } from '../../pages/GuidedWalkthroughPage';

interface Props {
  state: GuidedState;
  onChange: (updates: Partial<GuidedState>) => void;
  onNext: () => void;
  onBack: () => void;
}

function retentionLabel(days: number): string {
  if (days <= 31) return 'Minimum — good for troubleshooting only';
  if (days <= 90) return 'Standard — covers most investigations';
  if (days <= 365) return '1 year — NHS DSPT recommended';
  return '2 years — FCA minimum requirement';
}

export default function Step4Compliance({ state, onChange, onNext, onBack }: Props) {
  const freeDays = state.sentinelEnabled ? 90 : 31;

  return (
    <div>
      <h2 className="text-[22px] font-bold mb-1" style={{ color: 'var(--text-primary)' }}>How long do you need to keep your data?</h2>
      <p className="text-[14px] mb-8" style={{ color: 'var(--text-tertiary)' }}>
        Different regulations require different retention periods. We've set defaults based on your sector.
      </p>

      {/* Interactive retention slider */}
      <div className="rounded-lg p-5 mb-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-[13px] font-bold" style={{ color: 'var(--text-secondary)' }}>Interactive retention (searchable)</p>
            <p className="text-[12px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{retentionLabel(state.retentionDays)}</p>
          </div>
          <div className="text-right">
            <span className="text-[22px] font-bold font-mono text-[#0078d4]">{state.retentionDays}</span>
            <span className="text-[13px] ml-1" style={{ color: 'var(--text-tertiary)' }}>days</span>
          </div>
        </div>
        <input
          type="range"
          min={31}
          max={730}
          step={1}
          value={state.retentionDays}
          onChange={(e) => onChange({ retentionDays: parseInt(e.target.value, 10) })}
          className="w-full accent-[#0078d4]"
        />
        <div className="flex justify-between text-[10px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
          <span>31 days</span>
          <span>365 days</span>
          <span>730 days</span>
        </div>
        <p className="text-[11px] mt-2" style={{ color: 'var(--text-tertiary)' }}>
          First {freeDays} days free{state.sentinelEnabled ? ' (90 days free with Sentinel)' : ''}
        </p>
      </div>

      {/* Archive slider */}
      <div className="rounded-lg p-5 mb-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-[13px] font-bold" style={{ color: 'var(--text-secondary)' }}>Archive retention (compliance storage)</p>
            <p className="text-[12px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
              {state.archiveYears === 0 ? 'No archive — data deleted after interactive period' : `${state.archiveYears} year${state.archiveYears === 1 ? '' : 's'} in cold archive storage`}
            </p>
          </div>
          <div className="text-right">
            <span className="text-[22px] font-bold font-mono text-[#0078d4]">{state.archiveYears}</span>
            <span className="text-[13px] ml-1" style={{ color: 'var(--text-tertiary)' }}>years</span>
          </div>
        </div>
        <input
          type="range"
          min={0}
          max={7}
          step={1}
          value={state.archiveYears}
          onChange={(e) => onChange({ archiveYears: parseInt(e.target.value, 10) })}
          className="w-full accent-[#0078d4]"
        />
        <div className="flex justify-between text-[10px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
          <span>None</span>
          <span>3 years</span>
          <span>7 years</span>
        </div>
        <p className="text-[11px] mt-2" style={{ color: 'var(--text-tertiary)' }}>
          Archive data can only be searched using Search Jobs (additional cost applies)
        </p>
      </div>

      {/* Free data allowances */}
      <div className="rounded-lg p-5 mb-6" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
        <p className="text-[13px] font-bold mb-4" style={{ color: 'var(--text-secondary)' }}>Free data allowances</p>

        <div className="mb-4">
          <label className="block text-[12px] mb-1.5" style={{ color: 'var(--text-tertiary)' }}>
            Microsoft 365 E5 / A5 licences (number of users)
          </label>
          <input
            type="number"
            min={0}
            value={state.m365Licences}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              onChange({ m365Licences: isNaN(v) ? 0 : Math.max(0, v) });
            }}
            className="w-32 rounded text-[13px] py-1.5 px-2 font-mono focus:outline-none focus:border-[#0078d4]"
            style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid var(--outline-variant)', color: 'var(--text-secondary)' }}
          />
          <p className="text-[11px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
            E5 users get 5 MB/user/day of Sentinel data for free
          </p>
        </div>

        <label className="flex items-center gap-3 cursor-pointer">
          <button
            role="switch"
            aria-checked={state.defenderServersP2}
            onClick={() => onChange({ defenderServersP2: !state.defenderServersP2 })}
            className={[
              'relative w-10 h-5 rounded-full transition-all shrink-0',
              state.defenderServersP2 ? 'bg-[#0078d4]' : '',
            ].join(' ')}
          >
            <span className={[
              'absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all',
              state.defenderServersP2 ? 'left-5' : 'left-0.5',
            ].join(' ')} />
          </button>
          <div>
            <p className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>Microsoft Defender for Servers Plan 2</p>
            <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>Plan 2 includes 500 MB/server/day free</p>
          </div>
        </label>
      </div>

      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="px-5 py-3 rounded-lg text-[14px] transition-all flex items-center gap-2"
          style={{ border: '1px solid var(--outline-variant)', color: 'var(--text-tertiary)' }}
        >
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          Back
        </button>
        <button
          onClick={onNext}
          className="flex-1 py-3 rounded-lg font-semibold text-[14px] bg-[#0078d4] hover:bg-[#106ebe] text-white transition-all flex items-center justify-center gap-2"
        >
          Calculate My Estimate
          <span className="material-symbols-outlined text-[18px]">calculate</span>
        </button>
      </div>
    </div>
  );
}
